import {
  type CreateUsersCreateUserBody,
  type CreateUsersCreateUserResponse,
  type DeleteUsersByUserIdResponse,
  type GetUsersAccountsByUserIdResponse,
  type GetUsersByUserIdResponse,
  type GetUsersListFilters,
  type GetUsersListItem,
  type GetUsersListResponse,
  type GetUsersPlacesByUserIdParams,
  type GetUsersPlacesByUserIdResponse,
  type UpdateUsersByUserIdBody,
  type UpdateUsersByUserIdParams,
  type UpdateUsersByUserIdResponse,
  useCreateUsersAccountByUserIdByAccountId,
  useCreateUsersCreateUser,
  useCreateUsersPlacesByUserId,
  useDeleteUsersAccountsByUserId,
  useDeleteUsersByUserId,
  useDeleteUsersPlacesByUserId,
  useGetUsersAccountsByUserId,
  useGetUsersByUserId,
  useGetUsersList,
  useGetUsersPlacesByUserId,
  useUpdateUsersAccountByUserIdByAccountId,
  useUpdateUsersByUserId,
  useUpdateUsersPlaceByUserIdByPlaceId,
} from "@birdwatch/client";

import { useRefresh } from "@/features/shared/realtime-refresh";

// Constants
export const USER_PAGE_SIZE = 20;

/** The role `POST /users/create-user` gives a user created without one. */
const DEFAULT_CREATED_ROLE = "MEMBER";

// Type exports
export type User = GetUsersListItem;

export type {
  CreateUsersCreateUserBody,
  CreateUsersCreateUserResponse,
  DeleteUsersByUserIdResponse,
  GetUsersAccountsByUserIdResponse,
  GetUsersByUserIdResponse,
  GetUsersListResponse,
  GetUsersPlacesByUserIdParams,
  GetUsersPlacesByUserIdResponse,
  UpdateUsersByUserIdBody,
  UpdateUsersByUserIdParams,
  UpdateUsersByUserIdResponse,
};

// Hook exports
export {
  useGetUsersAccountsByUserId,
  useGetUsersList,
  useGetUsersPlacesByUserId,
};

// ── User mutations — each refreshes the events its users route announces ──

/**
 * Create a user (`POST /users/create-user`): the user, the roles they were
 * given, their account and their homes.
 */
export function useCreateUser() {
  const refresh = useRefresh();
  return useCreateUsersCreateUser({
    onSuccess: ({ userId }, { accountId, places, roles, role }) => {
      const roleKeys = roles?.length ? roles : [role ?? DEFAULT_CREATED_ROLE];
      const placeIds = (places ?? []).flatMap((place) =>
        place.placeId ? [place.placeId] : [],
      );
      return Promise.all([
        refresh("user.changed", { userId }),
        ...roleKeys.map((roleKey) => refresh("role.changed", { roleKey })),
        ...(accountId ? [refresh("account.changed", { accountId })] : []),
        ...placeIds.map((placeId) => refresh("household.changed", { placeId })),
      ]);
    },
  });
}

/** Edit a user (`PUT /users/:userId`); a non-empty `roles` list also moves those roles' counts. */
export function useUpdateUser() {
  const refresh = useRefresh();
  return useUpdateUsersByUserId({
    onSuccess: (_user, { params, body }) =>
      Promise.all([
        refresh("user.changed", params),
        ...(body.roles ?? []).map((roleKey) =>
          refresh("role.changed", { roleKey }),
        ),
      ]),
  });
}

/** Delete a user (`DELETE /users/:userId`). */
export function useDeleteUser() {
  const refresh = useRefresh();
  return useDeleteUsersByUserId({
    // Not awaited: the caller navigates away from the deleted user's detail.
    onSuccess: (_result, { params }) => {
      void refresh("user.changed", params);
    },
  });
}

/** Shared list builder — the page query and the sidebar prefetch must both build from this so their query keys match. */
export const useUsersBuilder = (options?: {
  search?: string;
  filters?: Partial<GetUsersListFilters>;
  sort?: string;
  includes?: string;
}) => {
  let builder = useGetUsersList()
    .take(USER_PAGE_SIZE)
    .sort(options?.sort || "-createdAt");

  if (options?.search) {
    builder = builder.search(options.search);
  }
  if (options?.filters) {
    builder = builder.filters(options.filters);
  }
  if (options?.includes) {
    builder = builder.includes(options.includes);
  }

  return builder;
};

/**
 * Infinite users-list query. Filters arrive pre-folded to the users-list DSL by
 * `buildListParams` (see `list/header/filters/_hooks/use-user-list-params`);
 * page size is set explicitly so the skeleton row count and the request stay in
 * sync (the server default is 25, the skeleton renders `USER_PAGE_SIZE`).
 */
export const useInfiniteUsers = (options?: {
  search?: string;
  filters?: Partial<GetUsersListFilters>;
  sort?: string;
  includes?: string;
}) => {
  return useUsersBuilder(options).getInfinite();
};

// useUserDetail - uses detail endpoint
export const useUserDetail = (userId: string | undefined) => {
  return useGetUsersByUserId({ userId: userId || "" })
    .query({ includes: "accounts,laborRate" })
    .get({ enabled: !!userId });
};

/** Accounts list + mutations; each refreshes the user and the accounts it touched. add/update take `{ params: { userId, accountId }, body }` at mutate() time. */
export function useUserAccounts(userId: string) {
  const refresh = useRefresh();
  const refreshUserAccounts = (accountIds: readonly string[]) =>
    Promise.all([
      refresh("user.changed", { userId }),
      ...accountIds.map((accountId) =>
        refresh("account.changed", { accountId }),
      ),
    ]);

  const { data: userAccounts = [], isLoading } = useGetUsersAccountsByUserId({
    userId,
  }).get({ enabled: !!userId });

  const add = useCreateUsersAccountByUserIdByAccountId({
    onSuccess: (_data, { params }) => refreshUserAccounts([params.accountId]),
  });
  const update = useUpdateUsersAccountByUserIdByAccountId({
    // A body `accountId` moves the link to that account: both lists change.
    onSuccess: (_data, { params, body }) =>
      refreshUserAccounts(
        body?.accountId
          ? [params.accountId, body.accountId]
          : [params.accountId],
      ),
  });
  const remove = useDeleteUsersAccountsByUserId({
    onSuccess: (_data, { body }) => refreshUserAccounts(body.accountIds),
  });

  return {
    userAccounts,
    isLoading,
    addUserAccount: add.mutateAsync,
    updateUserAccount: update.mutateAsync,
    removeUserAccount: (accountId: string) =>
      remove.mutateAsync({
        params: { userId },
        body: { accountIds: [accountId] },
      }),
    isAdding: add.isPending,
    isUpdating: update.isPending,
    isRemoving: remove.isPending,
  };
}

/** Places list + mutations; each refreshes the user and the homes it touched. Mutations take `{ params, body }` at mutate() time (`update` needs `params: { userId, placeId }`). */
export function useUserPlaces(userId: string) {
  const refresh = useRefresh();
  const refreshUserPlaces = (placeIds: readonly string[]) =>
    Promise.all([
      refresh("user.changed", { userId }),
      ...placeIds.map((placeId) => refresh("household.changed", { placeId })),
    ]);

  const { data: userPlaces = [], isLoading } = useGetUsersPlacesByUserId({
    userId,
  }).get({ enabled: !!userId });

  const add = useCreateUsersPlacesByUserId({
    onSuccess: (_data, { body }) =>
      refreshUserPlaces(body.places?.map((place) => place.placeId) ?? []),
  });
  const update = useUpdateUsersPlaceByUserIdByPlaceId({
    onSuccess: (_data, { params }) => refreshUserPlaces([params.placeId]),
  });
  const remove = useDeleteUsersPlacesByUserId({
    onSuccess: (_data, { body }) => refreshUserPlaces(body?.placeIds ?? []),
  });

  return {
    userPlaces,
    isLoading,
    addUserPlace: add.mutateAsync,
    updateUserPlace: update.mutateAsync,
    removeUserPlace: remove.mutateAsync,
    isAdding: add.isPending,
    isUpdating: update.isPending,
    isRemoving: remove.isPending,
  };
}
